/**
 * API utilities for SaollaServer admin panel
 */

const API_BASE = '/api/admin';

/**
 * Get auth token from localStorage
 */
function getToken() {
  return localStorage.getItem('admin_token');
}

/**
 * Make authenticated API request
 */
async function apiRequest(endpoint, options = {}) {
  const token = getToken();
  
  const config = {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
      ...options.headers,
    },
  };
  
  const response = await fetch(`${API_BASE}${endpoint}`, config);
  
  // Handle 401 - unauthorized
  if (response.status === 401) {
    localStorage.removeItem('admin_token');
    window.location.href = '/login';
    throw new Error('Unauthorized');
  }
  
  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.error || `HTTP ${response.status}`);
  }
  
  return data;
}

// ============= Auth =============

export async function login(password) {
  const response = await fetch(`${API_BASE}/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password }),
  });
  
  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.error || 'Login failed');
  }
  
  return data;
}

// ============= Logs =============

export async function getLogs(params = {}) {
  const query = new URLSearchParams();
  if (params.page) query.set('page', params.page);
  if (params.per_page) query.set('per_page', params.per_page);
  if (params.days) query.set('days', params.days);
  if (params.uid) query.set('uid', params.uid);
  if (params.type) query.set('type', params.type);
  
  const queryStr = query.toString();
  return apiRequest(`/logs${queryStr ? '?' + queryStr : ''}`);
}

export async function getLogDetails(logId) {
  return apiRequest(`/logs/${logId}`);
}

// ============= Polling Log =============

export async function startPollingLog(duration_seconds = 60) {
  return apiRequest('/polling-log/start', {
    method: 'POST',
    body: JSON.stringify({ duration_seconds }),
  });
}

export async function getPollingLog() {
  return apiRequest('/polling-log');
}

// ============= Devices =============

export async function getDevices() {
  return apiRequest('/devices');
}

export async function addDevice(uid, password) {
  return apiRequest('/devices', {
    method: 'POST',
    body: JSON.stringify({ uid, password }),
  });
}

export async function deleteDevice(uid) {
  return apiRequest(`/devices/${encodeURIComponent(uid)}`, {
    method: 'DELETE',
  });
}

// ============= Request Types =============

export async function getRequestTypes() {
  return apiRequest('/request-types');
}

export async function addRequestType(data) {
  return apiRequest('/request-types', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateRequestType(name, data) {
  return apiRequest(`/request-types/${encodeURIComponent(name)}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteRequestType(name) {
  return apiRequest(`/request-types/${encodeURIComponent(name)}`, {
    method: 'DELETE',
  });
}

// ============= Status & Cleanup =============

export async function getStatus() {
  return apiRequest('/status');
}

export async function runCleanup() {
  return apiRequest('/cleanup', { method: 'POST' });
}

// ============= AI Testing =============

export async function testAiRequest(requestType, prompt, data) {
  return apiRequest('/ai-test', {
    method: 'POST',
    body: JSON.stringify({
      request_type: requestType,
      prompt: prompt,
      data: data
    }),
  });
}

// ============= AI Testing (Streaming) =============

export async function testAiRequestWithDebug(requestType, prompt, data, onDebugUpdate) {
  const token = localStorage.getItem('admin_token');
  
  const response = await fetch('/api/admin/ai-test-stream', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
    },
    body: JSON.stringify({
      request_type: requestType,
      prompt: prompt,
      data: data
    }),
  });
  
  if (response.status === 401) {
    localStorage.removeItem('admin_token');
    window.location.href = '/login';
    throw new Error('Unauthorized');
  }
  
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || `HTTP ${response.status}`);
  }
  
  // Читаем streaming response
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  
  let fullDebugLog = '';
  let result = null;
  
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    
    const chunk = decoder.decode(value, { stream: true });
    const lines = chunk.split('\n');
    
    for (const line of lines) {
      if (!line.trim()) continue;
      
      if (line.startsWith('data: ')) {
        try {
          const data = JSON.parse(line.slice(6));
          
          if (data.type === 'debug') {
            fullDebugLog += data.message + '\n';
            if (onDebugUpdate) {
              onDebugUpdate(fullDebugLog);
            }
          } else if (data.type === 'result') {
            result = data;
          } else if (data.type === 'error') {
            throw new Error(data.error);
          }
        } catch (e) {
          if (e.message && !e.message.includes('JSON')) {
            throw e;
          }
        }
      }
    }
  }
  
  return { ...result, debug_log: fullDebugLog };
}

// ============= Settings =============

export async function getSettings() {
  return apiRequest('/settings');
}

export async function updateSettings(settings) {
  return apiRequest('/settings', {
    method: 'PUT',
    body: JSON.stringify(settings),
  });
}

// ============= LK Users =============

export async function getLkUsers() {
  return apiRequest('/lk-users');
}

export async function addLkUser(username, password, device_uid) {
  return apiRequest('/lk-users', {
    method: 'POST',
    body: JSON.stringify({ username, password, device_uid }),
  });
}

export async function updateLkUser(username, data) {
  return apiRequest(`/lk-users/${encodeURIComponent(username)}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteLkUser(username) {
  return apiRequest(`/lk-users/${encodeURIComponent(username)}`, {
    method: 'DELETE',
  });
}